import { useState } from "react"
import { useRelays } from "@/state/RelayContext"
import { simplifyUrl } from "@/utils/url"
import { AddRelayForm } from "./AddRelayForm"
import { RelayIcon } from "./Avatar"
import { Icon } from "./Icon"

/**
 * Relay management UI: the user's relays and relay sets, with create, rename,
 * delete and add/remove relay actions. Writes are published as kind 10012 and
 * kind 30002 events when signed in, and stored locally when browsing as a guest.
 */
export function RelayLibraryEditor() {
  const {
    favoriteRelays,
    relaySets,
    addFavoriteRelays,
    removeFavoriteRelays,
    createRelaySet,
    updateRelaySet,
    deleteRelaySet,
    addRelaysToSet,
    removeRelaysFromSet,
  } = useRelays()
  const [newSetName, setNewSetName] = useState("")
  const [renaming, setRenaming] = useState<string>()
  const [renameValue, setRenameValue] = useState("")
  const [addingTo, setAddingTo] = useState<string>()

  return (
    <div className="relay-library">
      <section className="settings-section">
        <div className="section-head">
          <h2>My relays</h2>
          <span className="badge">{favoriteRelays.length}</span>
        </div>
        <p className="muted small">
          These relays are read by the <em>Following</em> feed and used when publishing.
        </p>
        <AddRelayForm onAdd={(urls) => addFavoriteRelays(urls)} />
        <ul className="relay-list">
          {favoriteRelays.map((url) => (
            <li key={url} className="relay-list-item">
              <RelayIcon url={url} size={20} />
              <span className="relay-list-url">{simplifyUrl(url)}</span>
              <button
                type="button"
                className="icon-button small"
                aria-label={`Remove ${url}`}
                onClick={() => void removeFavoriteRelays([url])}
              >
                <Icon name="trash" size={15} />
              </button>
            </li>
          ))}
          {favoriteRelays.length === 0 && <li className="muted small">No relays yet.</li>}
        </ul>
      </section>

      <section className="settings-section">
        <div className="section-head">
          <h2>Relay sets</h2>
          <span className="badge">{relaySets.length}</span>
        </div>
        <p className="muted small">
          A relay set is a saved group of relays that becomes one switchable feed. Sets are published as
          kind 30002 events and referenced from your kind 10012 relay list.
        </p>

        <form
          className="inline-form"
          onSubmit={async (event) => {
            event.preventDefault()
            const created = await createRelaySet(newSetName)
            if (created) setNewSetName("")
          }}
        >
          <input
            value={newSetName}
            onChange={(event) => setNewSetName(event.target.value)}
            placeholder="New relay set name"
            aria-label="New relay set name"
          />
          <button type="submit" className="btn btn-primary" disabled={!newSetName.trim()}>
            Create set
          </button>
        </form>

        <div className="relay-set-list">
          {relaySets.map((set) => (
            <article key={set.id} className="relay-set-editor">
              <div className="relay-set-editor-head">
                {renaming === set.id ? (
                  <form
                    className="inline-form"
                    onSubmit={async (event) => {
                      event.preventDefault()
                      await updateRelaySet({ ...set, name: renameValue.trim() || set.name })
                      setRenaming(undefined)
                    }}
                  >
                    <input
                      value={renameValue}
                      onChange={(event) => setRenameValue(event.target.value)}
                      aria-label="Relay set name"
                      autoFocus
                    />
                    <button type="submit" className="btn btn-small btn-primary">
                      Save
                    </button>
                    <button
                      type="button"
                      className="btn btn-small btn-ghost"
                      onClick={() => setRenaming(undefined)}
                    >
                      Cancel
                    </button>
                  </form>
                ) : (
                  <>
                    <h3>
                      {set.name}
                      {set.preset && <span className="badge">preset</span>}
                    </h3>
                    <div className="relay-set-editor-tools">
                      <span className="muted small">{set.relayUrls.length} relays</span>
                      {!set.preset && (
                        <>
                          <button
                            type="button"
                            className="icon-button small"
                            aria-label={`Rename ${set.name}`}
                            onClick={() => {
                              setRenaming(set.id)
                              setRenameValue(set.name)
                            }}
                          >
                            <Icon name="pencil" size={15} />
                          </button>
                          <button
                            type="button"
                            className="icon-button small"
                            aria-label={`Delete ${set.name}`}
                            onClick={() => void deleteRelaySet(set.id)}
                          >
                            <Icon name="trash" size={15} />
                          </button>
                        </>
                      )}
                    </div>
                  </>
                )}
              </div>

              <ul className="relay-list">
                {set.relayUrls.map((url) => (
                  <li key={url} className="relay-list-item">
                    <RelayIcon url={url} size={18} />
                    <span className="relay-list-url">{simplifyUrl(url)}</span>
                    {!set.preset && (
                      <button
                        type="button"
                        className="icon-button small"
                        aria-label={`Remove ${url} from ${set.name}`}
                        onClick={() => void removeRelaysFromSet(set.id, [url])}
                      >
                        <Icon name="x" size={14} />
                      </button>
                    )}
                  </li>
                ))}
                {set.relayUrls.length === 0 && <li className="muted small">This set is empty.</li>}
              </ul>

              {!set.preset &&
                (addingTo === set.id ? (
                  <AddRelayForm
                    compact
                    buttonLabel="Add to set"
                    onAdd={async (urls) => {
                      await addRelaysToSet(set.id, urls)
                      setAddingTo(undefined)
                    }}
                  />
                ) : (
                  <button
                    type="button"
                    className="btn btn-small btn-ghost"
                    onClick={() => setAddingTo(set.id)}
                  >
                    <Icon name="plus" size={14} /> Add relay to this set
                  </button>
                ))}
            </article>
          ))}
          {relaySets.length === 0 && <p className="muted small">No relay sets yet.</p>}
        </div>
      </section>
    </div>
  )
}

